import { PabSession } from "@/services/api";
import { formatDisplayDate, parseDateInput } from "@/utils/formatDisplayDate";

export type MeetingTimeFilter = "all" | "upcoming" | "past";

export type MeetingListItem<T> = {
  meeting: T;
  status: "upcoming" | "completed";
};

/**
 * Builds a unified meeting list from upcoming + past lists based on the selected time filter.
 * Optionally accepts a predicate to filter both lists consistently.
 */
export const buildMeetingListByTimeFilter = <T,>({
  timeFilter,
  upcoming,
  past,
  predicate,
}: {
  timeFilter: MeetingTimeFilter;
  upcoming: T[];
  past: T[];
  predicate?: (meeting: T) => boolean;
}): MeetingListItem<T>[] => {
  const matches = predicate ?? (() => true);
  const allMeetings: MeetingListItem<T>[] = [];

  if (timeFilter === "all" || timeFilter === "upcoming") {
    allMeetings.push(
      ...upcoming.filter(matches).map((m) => ({ meeting: m, status: "upcoming" as const })),
    );
  }

  if (timeFilter === "all" || timeFilter === "past") {
    allMeetings.push(...past.filter(matches).map((m) => ({ meeting: m, status: "completed" as const })));
  }

  return allMeetings;
};

/**
 * Format meeting time for display, e.g. "Nov 6, 2026 · 11:30 AM - 12:30 PM (IST)"
 */
export const formatMeetingTime = (startTime: string, endTime: string): string =>
  formatDisplayDate(startTime, { end: endTime, length: "long" });

/**
 * Get PAB period from session
 * Returns formatted period string like "Nov 6, 2024 Advisory Board"
 */
export const getPabPeriod = (session: PabSession | null): string => {
  const from = formatDisplayDate(session?.activeFrom, { variant: "date", length: "long" });
  return from ? `${from} Advisory Board` : "Current Advisory Board";
};

/**
 * Format date for display, e.g. "November 6, 2026"
 */
export const formatDateForSessionPrep = (dateString: string): string =>
  formatDisplayDate(dateString, { variant: "date", length: "long" });

/**
 * Format time range for display, e.g. "11:30 AM - 12:30 PM (IST)"
 */
export const formatTimeRangeForSessionPrep = (startTime: string, endTime: string): string =>
  formatDisplayDate(startTime, { variant: "time", end: endTime });

/**
 * Checks if a URL is a Google Meet recording URL
 * Google Meet recordings can be:
 * - Google Drive links containing meeting recordings
 * @param url - The URL to check
 * @returns true if the URL appears to be a Google Meet recording
 */
export const isGoogleMeetRecordingUrl = (url: string): boolean => {
  if (!url || typeof url !== "string") {
    return false;
  }

  try {
    const parsedUrl = new URL(url);

    // Google Meet recordings are stored as video files in Google Drive
    return (
      parsedUrl.hostname === "drive.google.com" &&
      parsedUrl.pathname.startsWith("/file/d/")
    );
  } catch {
    // Invalid URL → definitely not a recording
    return false;
  }
};

 // Format date for display (e.g., "December 2, 2025")
 export const formatSessionDate = (dateString: string): string =>
  formatDisplayDate(dateString, { variant: "date", length: "long" });

 export const formatTimeWithDuration = (startTime: string, endTime: string): string => {
  const start = parseDateInput(startTime);
  const end = parseDateInput(endTime);
  const range = formatDisplayDate(startTime, { variant: "time", end: endTime });
  if (!start || !end) return range;
  const durationMinutes = Math.round((end.getTime() - start.getTime()) / (1000 * 60));
  return `${range} (${durationMinutes} min)`;
};




